import { addToast, Button, ColumnTable, Icon, Table } from '@octano/global-ui';
import { RefObject, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useActionKitImp } from '../../../components/ozmoit/common';
import { TranslationsKeys } from '../../../locales/translations';
import { notifyRequirementResultAction } from '../actions';
import { useRequirementStudentsLoader } from '../loaders/RequirementStudentsLoader';
import {
  ElectiveRequirementEnrollmentStatus,
  ElectiveRequirementItem,
  ElectiveRequirementStudentRow,
} from '../types';
import { RequirementResultModalRef } from './RequirementResultModal';
import RequirementBadge from './RequirementBadge';

export default function RequirementTable({
  item,
  modalRef,
  readOnly,
}: {
  item: ElectiveRequirementItem;
  enrollmentStatus: ElectiveRequirementEnrollmentStatus;
  modalRef: RefObject<RequirementResultModalRef>;
  readOnly: boolean;
}) {
  const { t } = useTranslation(TranslationsKeys.GRADUATION_REQUIREMENTS_REVIEW);
  const { loading, result, query, updateQuery } =
    useRequirementStudentsLoader();

  const { action: notify } = useActionKitImp(notifyRequirementResultAction, {
    onSuccess: () => {
      addToast({ color: 'success', text: t('modal.notifySuccess') });
    },
    onError: {
      unknown: () => {
        addToast({ color: 'danger', text: t('modal.notifyError') });
      },
    },
  });

  const columns = useMemo<
    (ColumnTable<ElectiveRequirementStudentRow> & { hidden?: boolean })[]
  >(
    () => [
      {
        columnName: 'studentName',
        headerText: t('table.headers.nameAndId'),
        cellFormat: ({ row }) => (
          <div>
            <div>{row.studentName}</div>
            <small className="text-muted d-block">
              {row.studentIdType
                ? `${row.studentIdType}: ${row.studentIdNumber}`
                : row.studentIdNumber}
            </small>
            {!!row.studentIdCountry && (
              <small className="text-muted d-block">
                {row.studentIdCountry}
              </small>
            )}
          </div>
        ),
      },
      {
        columnName: 'facultyName',
        headerText: t('table.headers.faculty'),
        cellFormat: ({ value }) => value ?? '-',
      },
      {
        columnName: 'graduationPeriodName',
        headerText: t('table.headers.graduationPeriod'),
        cellFormat: ({ value }) => value ?? '-',
      },
      {
        columnName: 'studyPlanVersionName',
        headerText: t('table.headers.studyPlan'),
      },
      {
        columnName: 'requirementGrade',
        headerText: t('table.headers.grade'),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        hidden: !item.allowGrades,
        cellFormat: ({ value }) => value ?? '-',
      },
      {
        columnName: 'requirementTitle',
        headerText: item.name,
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ row }) => (
          <div className="text-center">
            <span className="fw-700 text-center d-block">
              {row?.requirementTitle ?? '-'}
            </span>
            <span className="fw-700 text-center d-block">
              {row?.requirementDescription?.slice(0, 30) ?? '-'}
            </span>
          </div>
        ),
      },
      {
        columnName: 'enteredByName',
        headerText: t('table.headers.enteredBy'),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ row }) => (
          <div className="text-center">
            <span className="text-center d-block">
              {row?.enteredByName ?? '-'}
            </span>
          </div>
        ),
      },
      {
        columnName: 'detailFileId',
        headerText: t('table.headers.detail'),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ row }) => (
          <div className="d-flex justify-content-center" style={{ gap: 8 }}>
            <button
              type="button"
              className="btn p-0"
              onClick={() => modalRef.current?.openDetail(row)}
            >
              <Icon name="eye" />
            </button>
            {!!row.detailUrl && (
              <a
                href={row.detailUrl}
                target="_blank"
                rel="noreferrer"
                className="btn p-0"
              >
                <Icon name="download" />
              </a>
            )}
          </div>
        ),
      },
      {
        columnName: 'status',
        headerText: t('table.headers.status'),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        hidden: readOnly,
        width: 100,
        cellFormat: ({ value }) => <RequirementBadge status={value} />,
      },
      {
        columnName: 'action',
        headerText: t('table.headers.action'),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        hidden: readOnly,
        width: 100,
        cellFormat: ({ row }) => (
          <Button
            size="sm"
            text={t('table.actions.update')}
            onClick={() => modalRef.current?.openUpdate(row)}
          />
        ),
      },
      {
        columnName: 'notify',
        headerText: t('table.headers.notify'),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        hidden: readOnly,
        width: 100,
        cellFormat: ({ row }) => (
          <Button
            size="sm"
            outlined
            text={t('table.actions.notify')}
            disabled={row.status === 'pending'}
            onClick={() =>
              notify({
                typeId: item.typeId,
                studyPlanEnrollmentId: row.studyPlanEnrollmentId,
              })
            }
          />
        ),
      },
    ],
    [t, item.name, item.typeId, item.allowGrades, modalRef, notify, readOnly],
  );

  const rows = result?.success ? result.data.data : [];
  const total = result?.success ? result.data.total : 0;
  const totalPages = result?.success ? result.data.total_pages : 0;

  return (
    <div className="w-100 mt-4 d-block">
      <Table
        columns={columns.filter((c) => !c.hidden)}
        data={rows}
        isLoadingResults={loading}
        pagination={{
          itemsPerPage: query.items_per_page,
          currentPage: query.page,
          totalItems: total,
          totalPages,
          onChangePage: (nextPage) => updateQuery({ ...query, page: nextPage }),
        }}
      />
    </div>
  );
}
